// S27 管家稽核 /ai/butler-audit — butler(Aura)主動卡 / 相機存取軌 / 記憶稽核唯讀下鑽。
//
// 三區(adm-tabs 分頁)——主動卡稽核 / 相機存取軌 / 記憶稽核。頂部共用 owner_uid 篩選
// (選填,空=跨全 owner 近期);各區自帶次篩選(卡:status;記憶:category)。全唯讀
// (稽核面,無寫入)。經 restful /v3/admin/ai/butler/* 代理 cs 治理端點,回裸 dict。
//
// ⚠️ 時間戳兩制:主動卡 created_at/acted_at = epoch 微秒(formatTimestampUs);
//    記憶 / vision 的時間 = Postgres timestamp 字串(formatCsTime)。

import { useEffect, useRef, useState } from 'react';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { CopyableId } from '../../components/CopyableId';
import { formatTimestampUs } from '../../lib/format';
import {
  butlerCardKindLabel,
  butlerCardStatusLabel,
  butlerCardStatusTone,
  butlerCards,
  butlerMemories,
  butlerMemoryCategoryLabel,
  butlerMemoryCategoryTone,
  butlerVision,
  formatCsTime,
} from './aiOpsApi';
import type { AiButlerCard, AiButlerMemory, AiButlerVisionAccess } from './aiOpsApi';
import '../../features/_shared/shared.css';
import './aiOps.css';

const LIMIT = 200;

type Section = 'cards' | 'vision' | 'memories';

const SECTIONS: { value: Section; label: string; testid: string }[] = [
  { value: 'cards', label: '主動卡稽核', testid: 'butler-tab-cards' },
  { value: 'vision', label: '相機存取軌', testid: 'butler-tab-vision' },
  { value: 'memories', label: '記憶稽核', testid: 'butler-tab-memories' },
];

export default function ButlerAuditPage() {
  const [section, setSection] = useState<Section>('cards');

  // 共用 owner_uid 篩選(debounce 300ms)。
  const [ownerInput, setOwnerInput] = useState('');
  const [ownerUid, setOwnerUid] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setOwnerUid(ownerInput.trim()), 300);
    return () => clearTimeout(t);
  }, [ownerInput]);

  // 重新整理 nonce(僅重載當前分頁)。
  const [nonce, setNonce] = useState(0);
  const refresh = () => setNonce((n) => n + 1);

  return (
    <div data-testid="butler-audit-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">管家稽核</h1>
          <p className="page-subtitle">
            管家(butler / Aura)的主動卡、相機存取軌與長期記憶唯讀稽核。經 restful{' '}
            <span className="aiops-code">/v3/admin/ai/butler/*</span> 代理 cs 治理端點。全唯讀;不含任何寫入。
          </p>
        </div>
        <button className="btn-ghost" onClick={refresh}>
          重新整理
        </button>
      </div>

      <div className="filter-bar is-sticky" data-testid="butler-owner-bar">
        <label className="adm-field">
          <span className="text-secondary adm-field-label">擁有者 UID</span>
          <input
            type="search"
            placeholder="owner_uid(留空=跨全 owner 近期)"
            aria-label="依 owner_uid 篩選"
            value={ownerInput}
            onChange={(e) => setOwnerInput(e.target.value)}
            data-testid="butler-owner-filter"
          />
        </label>
        <div className="adm-toolbar-spacer" />
        {ownerInput && (
          <button className="btn-ghost btn-sm" onClick={() => setOwnerInput('')}>
            清除
          </button>
        )}
      </div>

      <div className="adm-tabs" data-testid="butler-tabs" style={{ marginBottom: 'var(--spacing-4)' }}>
        {SECTIONS.map((s) => (
          <button
            key={s.value}
            type="button"
            className={`adm-tab${section === s.value ? ' is-active' : ''}`}
            aria-pressed={section === s.value}
            onClick={() => setSection(s.value)}
            data-testid={s.testid}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'cards' && <CardsSection ownerUid={ownerUid} nonce={nonce} />}
      {section === 'vision' && <VisionSection ownerUid={ownerUid} nonce={nonce} />}
      {section === 'memories' && <MemoriesSection ownerUid={ownerUid} nonce={nonce} />}
    </div>
  );
}

// ── 通用 fetch hook ──────────────────────────────────────────────────────────

function useSection<T>(fetcher: (signal: AbortSignal) => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  // fetcher 每次 render 都是新的閉包;用 ref 存最新,effect 只靠 deps + reload 觸發。
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    fetcherRef.current(ac.signal)
      .then((r) => {
        if (!ac.signal.aborted) setData(r);
      })
      .catch((e) => {
        if (!ac.signal.aborted) {
          setError(e);
          setData(null);
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reload]);

  return { data, error, loading, retry: () => setReload((n) => n + 1) };
}

// ── 主動卡稽核 ────────────────────────────────────────────────────────────────

const CARD_STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'active', label: '有效' },
  { value: 'acted', label: '已採納' },
  { value: 'dismissed', label: '已忽略' },
];

function CardsSection({ ownerUid, nonce }: { ownerUid: string; nonce: number }) {
  const [status, setStatus] = useState('');
  const [openCard, setOpenCard] = useState<AiButlerCard | null>(null);

  const { data, error, loading, retry } = useSection(
    (signal) => butlerCards({ owner_uid: ownerUid || undefined, status: status || undefined, limit: LIMIT }, signal),
    [ownerUid, status, nonce]
  );

  const cards = data?.cards ?? null;
  const showInitialLoading = loading && cards === null;

  return (
    <>
      <div className="filter-bar" data-testid="butler-cards-filters">
        <label className="adm-field">
          <span className="text-secondary adm-field-label">狀態</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {CARD_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {showInitialLoading && <LoadingState label="載入主動卡…" />}
      {error != null && cards === null && <ErrorState error={error} onRetry={retry} />}

      {cards && cards.length === 0 && (
        <EmptyState title="沒有主動卡" detail={status || ownerUid ? '試著放寬狀態／owner 篩選。' : '目前尚無管家主動卡。'} />
      )}

      {cards && cards.length > 0 && (
        <div className="table-wrap" data-testid="butler-cards-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>卡片類型</th>
                <th>標題</th>
                <th>推送對象</th>
                <th>狀態</th>
                <th>時間</th>
              </tr>
            </thead>
            <tbody>
              {cards.map((c) => (
                <tr
                  key={c.card_id}
                  className="clickable"
                  tabIndex={0}
                  onClick={() => setOpenCard(c)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setOpenCard(c);
                  }}
                >
                  <td>
                    <span className="pill pill-neutral">{butlerCardKindLabel(c.kind)}</span>
                  </td>
                  <td className="adm-cell-strong aiops-cell-query" title={c.title}>
                    {c.title || '—'}
                  </td>
                  <td>
                    <CopyableId value={c.owner_uid} />
                  </td>
                  <td>
                    <span className={`pill ${butlerCardStatusTone(c.status)}`}>{butlerCardStatusLabel(c.status)}</span>
                  </td>
                  <td className="adm-mono">{formatTimestampUs(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="adm-pager">
            <span className="text-secondary">共 {cards.length} 筆</span>
          </div>
        </div>
      )}

      {openCard && <CardDrawer card={openCard} onClose={() => setOpenCard(null)} />}
    </>
  );
}

function jsonPretty(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function CardDrawer({ card, onClose }: { card: AiButlerCard; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hasTrigger = card.source_event != null || card.score != null || card.reason != null;

  return (
    <div className="aiops-drawer-scrim" role="presentation" onClick={onClose}>
      <aside
        className="aiops-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="主動卡詳情"
        onClick={(e) => e.stopPropagation()}
        data-testid="butler-card-drawer"
      >
        <div className="aiops-drawer-head">
          <h2 className="aiops-drawer-title">
            主動卡
            <span className="pill pill-neutral">{butlerCardKindLabel(card.kind)}</span>
            <span className={`pill ${butlerCardStatusTone(card.status)}`}>{butlerCardStatusLabel(card.status)}</span>
          </h2>
          <button ref={closeRef} className="btn-ghost btn-sm aiops-drawer-close" onClick={onClose} aria-label="關閉">
            關閉
          </button>
        </div>

        <div className="aiops-block">
          <span className="aiops-block-label">標題</span>
          <p className="aiops-quote">{card.title || '—'}</p>
        </div>

        <div className="aiops-block">
          <span className="aiops-block-label">內容</span>
          <p className="aiops-quote">{card.body || '—'}</p>
        </div>

        <div className="aiops-block">
          <span className="aiops-block-label">動作(actions）</span>
          {card.actions && card.actions.length > 0 ? (
            <div className="aiops-hits">
              {card.actions.map((a, i) => (
                <div className="aiops-hit" key={i}>
                  <div className="aiops-hit-source">{a.label ?? a.type ?? `動作 ${i + 1}`}</div>
                  {a.deep_link && (
                    <div className="aiops-hit-heading" style={{ gridColumn: '1 / -1' }}>
                      {a.deep_link}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="aiops-hint">無動作。</p>
          )}
        </div>

        <div className="aiops-block">
          <span className="aiops-block-label">觸發原因</span>
          {hasTrigger ? (
            <>
              <div className="aiops-meta-grid">
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">分數</span>
                  <span className="aiops-meta-v">{card.score != null ? card.score : '未記錄'}</span>
                </div>
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">說明</span>
                  <span className="aiops-meta-v">{card.reason ?? '未記錄'}</span>
                </div>
              </div>
              <span className="aiops-block-label" style={{ marginTop: 'var(--spacing-2)' }}>
                觸發事件(source_event）
              </span>
              {card.source_event != null ? (
                <pre className="aiops-quote" style={{ overflowX: 'auto' }}>
                  {jsonPretty(card.source_event)}
                </pre>
              ) : (
                <p className="aiops-hint">未記錄。</p>
              )}
            </>
          ) : (
            <p className="aiops-hint">未記錄觸發原因。</p>
          )}
        </div>

        <div className="aiops-block">
          <span className="aiops-block-label">度量</span>
          <div className="aiops-meta-grid">
            <div className="aiops-meta-item">
              <span className="aiops-meta-k">推送對象</span>
              <span className="aiops-meta-v">
                <CopyableId value={card.owner_uid} />
              </span>
            </div>
            <div className="aiops-meta-item">
              <span className="aiops-meta-k">卡片 ID</span>
              <span className="aiops-meta-v">
                <CopyableId value={card.card_id} />
              </span>
            </div>
            <div className="aiops-meta-item">
              <span className="aiops-meta-k">建立</span>
              <span className="aiops-meta-v">{formatTimestampUs(card.created_at)}</span>
            </div>
            <div className="aiops-meta-item">
              <span className="aiops-meta-k">採納/忽略</span>
              <span className="aiops-meta-v">{card.acted_at ? formatTimestampUs(card.acted_at) : '—'}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

// ── 相機存取軌 ────────────────────────────────────────────────────────────────

function VisionSection({ ownerUid, nonce }: { ownerUid: string; nonce: number }) {
  const { data, error, loading, retry } = useSection(
    (signal) => butlerVision({ owner_uid: ownerUid || undefined, limit: LIMIT }, signal),
    [ownerUid, nonce]
  );

  const rows = data?.vision ?? null;
  const showInitialLoading = loading && rows === null;

  return (
    <>
      {showInitialLoading && <LoadingState label="載入相機存取軌…" />}
      {error != null && rows === null && <ErrorState error={error} onRetry={retry} />}

      {rows && rows.length === 0 && <EmptyState title="尚無相機存取紀錄" detail="目前沒有管家 vision 相機存取軌。" />}

      {rows && rows.length > 0 && (
        <div className="table-wrap" data-testid="butler-vision-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>擁有者</th>
                <th>裝置</th>
                <th>來源</th>
                <th>事件 ID</th>
                <th>事件時間</th>
                <th>觸發時間</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v: AiButlerVisionAccess, i) => (
                <tr key={`${v.owner_uid}-${v.device_id}-${v.eid}-${i}`}>
                  <td>
                    <CopyableId value={v.owner_uid} />
                  </td>
                  <td>
                    <CopyableId value={v.device_id} />
                  </td>
                  <td className="text-secondary">{v.source || '—'}</td>
                  <td className="adm-mono">{v.eid || '—'}</td>
                  <td className="adm-mono">{formatCsTime(v.event_ts)}</td>
                  <td className="adm-mono">{formatCsTime(v.triggered_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="adm-pager">
            <span className="text-secondary">共 {rows.length} 筆</span>
          </div>
        </div>
      )}
    </>
  );
}

// ── 記憶稽核 ──────────────────────────────────────────────────────────────────

const MEMORY_CATEGORY_OPTIONS = [
  { value: '', label: '全部類別' },
  { value: 'preference', label: '偏好' },
  { value: 'fact', label: '事實' },
  { value: 'routine', label: '作息' },
  { value: 'contact', label: '聯絡人' },
  { value: 'note', label: '備註' },
  { value: 'warning', label: '警示' },
];

function MemoriesSection({ ownerUid, nonce }: { ownerUid: string; nonce: number }) {
  const [category, setCategory] = useState('');

  const { data, error, loading, retry } = useSection(
    (signal) =>
      butlerMemories({ owner_uid: ownerUid || undefined, category: category || undefined, limit: LIMIT }, signal),
    [ownerUid, category, nonce]
  );

  const rows = data?.memories ?? null;
  const showInitialLoading = loading && rows === null;

  return (
    <>
      <div className="filter-bar" data-testid="butler-memories-filters">
        <label className="adm-field">
          <span className="text-secondary adm-field-label">記憶類別</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {MEMORY_CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {showInitialLoading && <LoadingState label="載入記憶…" />}
      {error != null && rows === null && <ErrorState error={error} onRetry={retry} />}

      {rows && rows.length === 0 && (
        <EmptyState title="沒有記憶" detail={category || ownerUid ? '試著放寬類別／owner 篩選。' : '目前尚無管家長期記憶。'} />
      )}

      {rows && rows.length > 0 && (
        <div className="table-wrap" data-testid="butler-memories-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>擁有者</th>
                <th>成員</th>
                <th>記憶類別</th>
                <th>內容</th>
                <th>釘選</th>
                <th>時間</th>
                <th>來源對話</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m: AiButlerMemory) => (
                <tr key={m.memory_id}>
                  <td>
                    <CopyableId value={m.owner_uid} />
                  </td>
                  <td className="text-secondary">{m.member_id || '—'}</td>
                  <td>
                    <span className={`pill ${butlerMemoryCategoryTone(m.category)}`}>
                      {butlerMemoryCategoryLabel(m.category)}
                    </span>
                  </td>
                  <td className="adm-cell-strong aiops-cell-query" title={m.trigger ? `${m.content}（觸發:${m.trigger}）` : m.content}>
                    {m.content || '—'}
                  </td>
                  <td>{m.is_pinned ? <span className="pill pill-info">已釘選</span> : <span className="aiops-muted">—</span>}</td>
                  <td className="adm-mono">{formatCsTime(m.created_at)}</td>
                  <td>
                    {m.source_conversation_id ? (
                      <CopyableId value={m.source_conversation_id} />
                    ) : m.source_trace_id ? (
                      <CopyableId value={m.source_trace_id} />
                    ) : (
                      <span className="aiops-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="adm-pager">
            <span className="text-secondary">共 {rows.length} 筆</span>
          </div>
        </div>
      )}
    </>
  );
}
